'use client';

import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import type { Document } from '@contentful/rich-text-types';
import Image from 'next/image';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { LinkButton } from '@/components/ui/Button/LinkButton';
import type { StoreHit } from '@/lib/algolia';
import type {
  CMSFindAStoreData,
  CMSPlaceholderStores as CMSPlaceholderStoresType,
} from '@/lib/cms/types';
import { BoutiqueCard } from '../BoutiqueCard';
import { Heading, type HeadingData } from '../Heading';

import styles from './PlaceholderStores.module.scss';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';

// ============================================
// Props
// ============================================

export interface PlaceholderStoresProps extends Omit<
  CMSPlaceholderStoresType,
  '__typename' | 'sys' | 'internalName'
> {
  /** Store data from Algolia (passed via extraData) */
  stores?: StoreHit[];
  /** CMS index for test IDs */
  cmsIndex?: number;
}

// ============================================
// FindAStore Card Component
// Matches Breitling design with image + yellow button
// ============================================

interface FindAStoreCardProps {
  findAStore?: CMSFindAStoreData | null;
}

function FindAStoreCard({ findAStore }: FindAStoreCardProps) {
  const href = findAStore?.url || 'https://store.breitling.com/';
  const buttonText = findAStore?.urlText || 'Find a Store';
  const cardImage = findAStore?.cardImage?.url;
  const backgroundColor = findAStore?.backgroundColor;

  // Extract plain text from rich text JSON
  let textContent: string | null = null;
  if (findAStore?.text?.json) {
    try {
      textContent = documentToPlainTextString(findAStore.text.json as Document);
    } catch {
      // Fallback if rich text parsing fails
      textContent = null;
    }
  }

  return (
    <article
      className={styles.findAStoreCard}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      {/* Image section */}
      <div className={styles.findAStoreImageWrapper}>
        {cardImage ? (
          <Image
            src={cardImage}
            alt="Find a store"
            fill
            className={styles.findAStoreImage}
            sizes="(max-width: 768px) 100vw, 400px"
          />
        ) : (
          // Placeholder with Breitling logo/icon
          <div className={styles.findAStoreImagePlaceholder}>
            <svg viewBox="0 0 100 100" className={styles.findAStorePlaceholderIcon}>
              <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="2" />
              <text x="50" y="55" textAnchor="middle" fontSize="12" fill="currentColor">
                BREITLING
              </text>
            </svg>
          </div>
        )}
      </div>

      {/* Info section */}
      <div className={styles.findAStoreInfo}>
        {textContent && <p className={styles.findAStoreText}>{textContent}</p>}
        <div className={styles.findAStoreCta}>
          <LinkButton href={href} variant="primary" size="md" fullWidth external>
            {buttonText}
          </LinkButton>
        </div>
      </div>
    </article>
  );
}

// ============================================
// Main Component
// ============================================

/**
 * PlaceholderStores Component
 *
 * Displays a slider of boutique cards with a "Find a Store" CTA.
 * Store data is fetched server-side via the orchestrator and passed via extraData.
 *
 * Design: White section background, cards have light grey backgrounds
 */
export function PlaceholderStores({
  type: _type = 'Default',
  numberOfStores = 3,
  backgroundColor: _backgroundColor,
  heading,
  findAStore,
  stores = [],
  cmsIndex,
}: PlaceholderStoresProps) {
  // Build heading data if present - include all CMS fields for colors
  const headingData: HeadingData | null = heading?.text
    ? {
        text: heading.text,
        headingType: heading.headingType || 'h2',
        textAlignment: 'center',
        mainColor: heading.mainColor || '#000000',
        highlightColor: heading.highlightColor || '#FFC72C', // Breitling gold
      }
    : null;

  // No stores - show simple FindAStore only
  if (!stores || stores.length === 0) {
    return (
      <section
        className={styles.container}
        data-testid={
          cmsIndex !== undefined ? `placeholder-stores-${cmsIndex}` : 'PlaceholderStores'
        }
      >
        <div className={styles.inner}>
          {headingData && (
            <div className={styles.headingWrapper}>
              <Heading Heading={headingData} />
            </div>
          )}
          <div className={styles.emptyState}>
            <FindAStoreCard findAStore={findAStore} />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={styles.container}
      data-testid={cmsIndex !== undefined ? `placeholder-stores-${cmsIndex}` : 'PlaceholderStores'}
    >
      <div className={styles.inner}>
        {headingData && (
          <div className={styles.headingWrapper}>
            <Heading Heading={headingData} />
          </div>
        )}

        <div className={styles.sliderWrapper}>
          <Swiper
            modules={[Navigation]}
            spaceBetween={12}
            slidesPerView={1.2}
            navigation
            breakpoints={{
              640: {
                slidesPerView: 2,
                spaceBetween: 12,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 12,
              },
              1280: {
                slidesPerView: (numberOfStores ?? 3) + 1, // +1 for FindAStore card
                spaceBetween: 12,
              },
            }}
            className={styles.swiper}
          >
            {stores.map((store, index) => (
              <SwiperSlide key={store.objectID || index} className={styles.slide}>
                <BoutiqueCard store={store} index={index} />
              </SwiperSlide>
            ))}

            {/* FindAStore card at the end */}
            <SwiperSlide className={styles.slide}>
              <FindAStoreCard findAStore={findAStore} />
            </SwiperSlide>
          </Swiper>
        </div>
      </div>
    </section>
  );
}

PlaceholderStores.displayName = 'PlaceholderStores';
